import React, { useState } from 'react';
import { documentService, GenerateOfferLetterData } from '@services/document.service';
import { track } from '@services/analytics';

interface Props {
  propertyId: string;
  askingPrice?: number;
  offreConseillee?: number;
  agencyName?: string;
  onClose: () => void;
}

const defaultDeadline = () => {
  const d = new Date();
  d.setDate(d.getDate() + 10);
  return d.toISOString().substring(0, 10);
};

export const OfferLetterModal: React.FC<Props> = ({
  propertyId,
  askingPrice,
  offreConseillee,
  agencyName,
  onClose,
}) => {
  const suggestedPrice = offreConseillee
    ? Math.round(offreConseillee)
    : askingPrice ?? 0;

  const [offeredPrice, setOfferedPrice] = useState(suggestedPrice);
  const [sellerName, setSellerName] = useState(agencyName ?? '');
  const [sellerAddress, setSellerAddress] = useState('');
  const [sellerPhone, setSellerPhone] = useState('');
  const [depositAmount, setDepositAmount] = useState(Math.round(suggestedPrice * 0.1));
  const [paymentTerms, setPaymentTerms] = useState('');
  const [loanCondition, setLoanCondition] = useState(false);
  const [loanAmount, setLoanAmount] = useState('');
  const [loanDuration, setLoanDuration] = useState('');
  const [loanMaxRate, setLoanMaxRate] = useState('');
  const [noEncumbranceCondition, setNoEncumbranceCondition] = useState(false);
  const [otherConditions, setOtherConditions] = useState('');
  const [acceptanceDeadline, setAcceptanceDeadline] = useState(defaultDeadline());

  // Justification du prix
  const [legalRestrictionsEnabled, setLegalRestrictionsEnabled] = useState(false);
  const [legalRestrictions, setLegalRestrictions] = useState('');
  const [ppeWorksEnabled, setPpeWorksEnabled] = useState(false);
  const [ppeWorks, setPpeWorks] = useState('');
  const [fiscalValueEnabled, setFiscalValueEnabled] = useState(false);
  const [fiscalValue, setFiscalValue] = useState('');
  const [otherPriceElementsEnabled, setOtherPriceElementsEnabled] = useState(false);
  const [otherPriceElements, setOtherPriceElements] = useState('');

  const [isGenerating, setIsGenerating] = useState(false);
  const [genError, setGenError] = useState<string | null>(null);

  const handleOfferedPriceChange = (value: number) => {
    setOfferedPrice(value);
    setDepositAmount(Math.round(value * 0.1));
  };

  const handleGenerate = async () => {
    setIsGenerating(true);
    setGenError(null);
    try {
      const payload: GenerateOfferLetterData = {
        offeredPrice,
        sellerName: sellerName || undefined,
        sellerAddress: sellerAddress || undefined,
        sellerPhone: sellerPhone || undefined,
        depositAmount,
        paymentTerms: paymentTerms || undefined,
        loanCondition,
        loanAmount: loanCondition && loanAmount !== '' ? Number(loanAmount) : undefined,
        loanDuration: loanCondition && loanDuration !== '' ? Number(loanDuration) : undefined,
        loanMaxRate: loanCondition && loanMaxRate !== '' ? Number(loanMaxRate) : undefined,
        noEncumbranceCondition,
        otherConditions: otherConditions || undefined,
        legalRestrictions: legalRestrictionsEnabled && legalRestrictions ? legalRestrictions : undefined,
        ppeWorks: ppeWorksEnabled && ppeWorks ? ppeWorks : undefined,
        fiscalValue: fiscalValueEnabled && fiscalValue ? fiscalValue : undefined,
        otherPriceElements: otherPriceElementsEnabled && otherPriceElements ? otherPriceElements : undefined,
        acceptanceDeadline,
      };

      const result = await documentService.generateOfferLetter(propertyId, payload);

      // Trigger download via fetch+blob — URL absolue pour bypasser le proxy Vite
      const apiBase = (import.meta.env.VITE_API_URL as string) || '/api';
      const token = localStorage.getItem('token');
      const dlRes = await fetch(`${apiBase}/files/${result.fileId}/download`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (dlRes.ok) {
        const blob = await dlRes.blob();
        const blobUrl = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = blobUrl;
        a.download = result.filename;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(blobUrl);
      }

      track('offer-generated', { price: offeredPrice });
      onClose();
    } catch (err: any) {
      setGenError(err?.response?.data?.message || err?.message || 'Erreur lors de la génération.');
    } finally {
      setIsGenerating(false);
    }
  };

  const chf = (n: number) =>
    n.toLocaleString('fr-CH', { minimumFractionDigits: 0 }) + ' CHF';

  const inputClass =
    'w-full px-3 py-1.5 border border-gray-300 dark:border-gray-600 rounded text-sm ' +
    'bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 ' +
    'focus:outline-none focus:ring-2 focus:ring-blue-500';
  const labelClass = 'block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1';
  const sectionClass = 'border-t border-gray-100 dark:border-gray-700 pt-4';

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
          <div>
            <h2 className="text-lg font-bold text-gray-900 dark:text-gray-100">
              Générer une offre d'achat
            </h2>
            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
              Le document Word sera téléchargé et sauvegardé dans les fichiers de cette propriété.
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <svg className="w-5 h-5 text-gray-500" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        <div className="p-5 space-y-5">
          {/* Prix proposé */}
          <div>
            <label className={labelClass}>
              Prix proposé (CHF)
              {offreConseillee && (
                <span className="ml-2 text-xs text-blue-600 dark:text-blue-400 font-normal">
                  Offre conseillée : {chf(Math.round(offreConseillee))}
                  {askingPrice && offreConseillee < askingPrice && (
                    <span className="text-orange-500 ml-1">
                      ({Math.round((1 - offreConseillee / askingPrice) * 100)}% sous le prix demandé)
                    </span>
                  )}
                </span>
              )}
            </label>
            <input
              type="number"
              className={inputClass}
              value={offeredPrice}
              min={1}
              step={1000}
              onChange={e => handleOfferedPriceChange(Number(e.target.value))}
            />
            {askingPrice && (
              <div className="mt-1 flex items-center gap-2">
                <input
                  type="range"
                  min={Math.round(askingPrice * 0.7)}
                  max={askingPrice}
                  step={1000}
                  value={offeredPrice}
                  onChange={e => handleOfferedPriceChange(Number(e.target.value))}
                  className="flex-1 h-1.5 accent-blue-600"
                />
                <span className="text-xs text-gray-500">
                  {askingPrice ? `Prix demandé : ${chf(askingPrice)}` : ''}
                </span>
              </div>
            )}
          </div>

          {/* Vendeur */}
          <div className={sectionClass}>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
              Vendeur / Mandataire
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Nom / Agence</label>
                <input type="text" className={inputClass} value={sellerName}
                  onChange={e => setSellerName(e.target.value)}
                  placeholder="Agence ImmoNE Sàrl" />
              </div>
              <div>
                <label className={labelClass}>Téléphone</label>
                <input type="tel" className={inputClass} value={sellerPhone}
                  onChange={e => setSellerPhone(e.target.value)}
                  placeholder="+41 32 000 00 00" />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Adresse</label>
                <input type="text" className={inputClass} value={sellerAddress}
                  onChange={e => setSellerAddress(e.target.value)}
                  placeholder="Rue du Temple 10, 2000 Neuchâtel" />
              </div>
            </div>
          </div>

          {/* Modalités financières */}
          <div className={sectionClass}>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
              Modalités financières
            </h3>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <label className={labelClass}>Dépôt de garantie (CHF)</label>
                <input type="number" className={inputClass} value={depositAmount}
                  min={0} step={1000}
                  onChange={e => setDepositAmount(Number(e.target.value))} />
              </div>
              <div className="sm:col-span-2">
                <label className={labelClass}>Modalités de paiement (optionnel)</label>
                <input type="text" className={inputClass} value={paymentTerms}
                  onChange={e => setPaymentTerms(e.target.value)}
                  placeholder="Paiement comptant à la signature de l'acte authentique" />
              </div>
            </div>
          </div>

          {/* Justification du prix */}
          <div className={sectionClass}>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-1 flex items-center gap-2">
              Justification du prix proposé
              <span className="text-xs font-normal text-gray-400 dark:text-gray-500 bg-gray-100 dark:bg-gray-700 px-1.5 py-0.5 rounded">
                optionnel
              </span>
            </h3>
            <p className="text-xs text-gray-500 dark:text-gray-400 mb-3">
              Cochez les éléments qui justifient votre offre en dessous du prix demandé.
            </p>
            <div className="space-y-3">
              {/* Restrictions légales */}
              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={legalRestrictionsEnabled}
                    onChange={e => setLegalRestrictionsEnabled(e.target.checked)}
                    className="mt-0.5 accent-blue-600" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Restrictions légales</span>
                </label>
                {legalRestrictionsEnabled && (
                  <textarea
                    className={inputClass + ' resize-none mt-2 ml-6'}
                    rows={2}
                    value={legalRestrictions}
                    onChange={e => setLegalRestrictions(e.target.value)}
                    placeholder="Ex : Plan d'affectation communal limitant la constructibilité (art. XX, loi sur l'aménagement du territoire)"
                    autoFocus
                  />
                )}
              </div>

              {/* Travaux PPE */}
              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={ppeWorksEnabled}
                    onChange={e => setPpeWorksEnabled(e.target.checked)}
                    className="mt-0.5 accent-blue-600" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Travaux prévus dans la PPE</span>
                </label>
                {ppeWorksEnabled && (
                  <textarea
                    className={inputClass + ' resize-none mt-2 ml-6'}
                    rows={2}
                    value={ppeWorks}
                    onChange={e => setPpeWorks(e.target.value)}
                    placeholder="Ex : Décision de l'assemblée des copropriétaires du [date], montant estimé : CHF ___"
                    autoFocus
                  />
                )}
              </div>

              {/* Valeur fiscale */}
              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={fiscalValueEnabled}
                    onChange={e => setFiscalValueEnabled(e.target.checked)}
                    className="mt-0.5 accent-blue-600" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Valeur fiscale ou expertise</span>
                </label>
                {fiscalValueEnabled && (
                  <textarea
                    className={inputClass + ' resize-none mt-2 ml-6'}
                    rows={2}
                    value={fiscalValue}
                    onChange={e => setFiscalValue(e.target.value)}
                    placeholder="Ex : Valeur fiscale 2026 : CHF ___, selon avis de l'administration cantonale"
                    autoFocus
                  />
                )}
              </div>

              {/* Autres éléments */}
              <div>
                <label className="flex items-start gap-2 cursor-pointer">
                  <input type="checkbox" checked={otherPriceElementsEnabled}
                    onChange={e => setOtherPriceElementsEnabled(e.target.checked)}
                    className="mt-0.5 accent-blue-600" />
                  <span className="text-sm text-gray-700 dark:text-gray-300">Autres éléments</span>
                </label>
                {otherPriceElementsEnabled && (
                  <textarea
                    className={inputClass + ' resize-none mt-2 ml-6'}
                    rows={2}
                    value={otherPriceElements}
                    onChange={e => setOtherPriceElements(e.target.value)}
                    placeholder="Ex : État du bien nécessitant des réparations majeures (toiture, électricité)"
                    autoFocus
                  />
                )}
              </div>
            </div>
          </div>

          {/* Conditions suspensives */}
          <div className={sectionClass}>
            <h3 className="text-sm font-semibold text-gray-800 dark:text-gray-200 mb-3">
              Conditions suspensives
            </h3>
            <div className="space-y-3">
              {/* Prêt bancaire */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={loanCondition}
                  onChange={e => setLoanCondition(e.target.checked)}
                  className="mt-0.5 accent-blue-600" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Obtention d'un prêt hypothécaire
                </span>
              </label>
              {loanCondition && (
                <div className="ml-6 grid grid-cols-3 gap-3">
                  <div>
                    <label className={labelClass}>Montant (CHF) <span className="font-normal text-gray-400">(optionnel)</span></label>
                    <input type="number" className={inputClass} value={loanAmount}
                      min={0} step={10000} placeholder="Ex : 320 000"
                      onChange={e => setLoanAmount(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Durée (ans) <span className="font-normal text-gray-400">(optionnel)</span></label>
                    <input type="number" className={inputClass} value={loanDuration}
                      min={1} max={30} placeholder="Ex : 25"
                      onChange={e => setLoanDuration(e.target.value)} />
                  </div>
                  <div>
                    <label className={labelClass}>Taux max (%) <span className="font-normal text-gray-400">(optionnel)</span></label>
                    <input type="number" className={inputClass} value={loanMaxRate}
                      min={0} step={0.1} placeholder="Ex : 2.5"
                      onChange={e => setLoanMaxRate(e.target.value)} />
                  </div>
                </div>
              )}

              {/* Absence de servitudes */}
              <label className="flex items-start gap-2 cursor-pointer">
                <input type="checkbox" checked={noEncumbranceCondition}
                  onChange={e => setNoEncumbranceCondition(e.target.checked)}
                  className="mt-0.5 accent-blue-600" />
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Absence de servitudes ou charges non déclarées
                </span>
              </label>

              {/* Autres */}
              <div>
                <label className={labelClass}>Autres conditions (optionnel)</label>
                <textarea
                  className={inputClass + ' resize-none'}
                  rows={2}
                  value={otherConditions}
                  onChange={e => setOtherConditions(e.target.value)}
                  placeholder="Ex : résultat satisfaisant d'un diagnostic PPE..."
                />
              </div>
            </div>
          </div>

          {/* Date limite */}
          <div className={sectionClass}>
            <label className={labelClass}>Date limite d'acceptation</label>
            <input
              type="date"
              className={inputClass + ' max-w-xs'}
              value={acceptanceDeadline}
              onChange={e => setAcceptanceDeadline(e.target.value)}
            />
          </div>

          {/* Error */}
          {genError && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-md text-red-700 text-sm">
              {genError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div className="flex justify-end gap-3 p-5 border-t border-gray-200 dark:border-gray-700">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg"
          >
            Annuler
          </button>
          <button
            onClick={handleGenerate}
            disabled={isGenerating || offeredPrice <= 0}
            className="flex items-center gap-2 px-4 py-2 text-sm text-white bg-blue-600 hover:bg-blue-700 disabled:opacity-50 rounded-lg font-medium"
          >
            {isGenerating ? (
              <>
                <svg className="animate-spin w-4 h-4" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                </svg>
                Génération...
              </>
            ) : (
              <>
                <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 10v6m0 0l-3-3m3 3l3-3m2 8H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                </svg>
                Générer le document Word
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};
